import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "../../../../i18n/LanguageContext";
import URLSafetyAPI from "../../services/URLSafetyAPI";
import ScamReportAPI from "../../services/ScamReportAPI";
import { useUser } from "../../context/UserContext";
import { useLoading } from "../../../../contexts/LoadingContext";
import ReadOnlyBanner from "../../common/ReadOnlyBanner";
import URLSafetyAnalysisDesktop from "./URLSafetyAnalysisDesktop";
import URLSafetyAnalysisMobile from "./URLSafetyAnalysisMobile";

const URLSafetyAnalysis = ({ setHeroContent }) => {
  const { showLoading, hideLoading } = useLoading();
  const { loggedInUser } = useUser();
  const { t } = useTranslation();

  const [url, setUrl] = useState("");
  const [context, setContext] = useState("");
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState(null);
  const [reportId, setReportId] = useState(null);

  const isMobile = () => window.innerWidth <= 768;
  const [showMobileView, setShowMobileView] = useState(isMobile());

  useEffect(() => {
    const handleResize = () => setShowMobileView(isMobile());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const normalizeUrl = (rawUrl) => {
    if (!rawUrl || !rawUrl.trim()) return rawUrl;
    const trimmedUrl = rawUrl.trim();
    if (trimmedUrl.startsWith("http://") || trimmedUrl.startsWith("https://")) {
      return trimmedUrl;
    }
    return `https://${trimmedUrl}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!url.trim()) {
      toast.error(t("urlSafety.toast.enterUrl"));
      return;
    }

    if (!loggedInUser) {
      toast.error(t("urlSafety.toast.loginRequired"));
      return;
    }

    showLoading(
      "Analyzing URL",
      "Please wait while we check the safety of this URL...",
    );
    setError(null);
    setAnalysisResult(null);

    try {
      const normalizedUrl = normalizeUrl(url);
      const result = await URLSafetyAPI.analyzeURL(
        normalizedUrl,
        context.trim() || null,
        {
          pollInterval: 3000,
          maxPolls: 40,
          onStatusUpdate: (status, pollCount) => {
            if (pollCount > 5) {
              showLoading(
                "Analyzing URL",
                "AI is performing a deep web search on this URL. Almost done...",
              );
            }
          },
        },
      );

      setAnalysisResult(result);
      hideLoading();

      if (result.isScam === true) {
        try {
          const saveResponse = await ScamReportAPI.saveReport({
            analysisType: "URL_SAFETY",
            analysisTitle: "Potentially Dangerous URL Detected",
            analysisDetail: `URL: ${normalizedUrl}`,
            analysisInput: normalizedUrl,
            context: context?.trim() || undefined,
            isScam: result.isScam,
            confidence: result.confidence,
            riskLevel: result.riskLevel,
            explanation: result.explanation,
            suggestions: result.suggestions,
          });
          if (saveResponse?.reportId) setReportId(saveResponse.reportId);
        } catch (saveErr) {
          console.error("Failed to save scam report:", saveErr);
        }
      }

      if (result.isScam === false) {
        toast.success(t("urlSafety.toast.safe"));
      } else if (result.isScam === true) {
        toast.error(t("urlSafety.toast.dangerous"));
      } else {
        toast("Analysis completed with low confidence", { icon: "⚠️" });
      }
    } catch (err) {
      console.error("Error analyzing URL:", err);
      hideLoading();
      setError(
        err.response?.data?.message ||
          "An error occurred while analyzing the URL. Please try again.",
      );
      toast.error(t("urlSafety.toast.failed"));
    }
  };

  const handleClear = () => {
    setUrl("");
    setContext("");
    setAnalysisResult(null);
    setError(null);
  };

  const sharedProps = {
    url,
    setUrl,
    context,
    setContext,
    analysisResult,
    error,
    reportId,
    handleSubmit,
    handleClear,
    setHeroContent,
  };

  return (
    <div
      className={`common-container url-safety-analysis-container${showMobileView ? " urlsa-mobile-active" : ""}`}
    >
      <ReadOnlyBanner />
      {showMobileView ? (
        <URLSafetyAnalysisMobile {...sharedProps} />
      ) : (
        <URLSafetyAnalysisDesktop {...sharedProps} />
      )}
    </div>
  );
};

export default URLSafetyAnalysis;
